export type AccessProgramResponse = Array<{
  id?: number;
  name?: string;
  access?: boolean;
  projects?: Array<{
    id?: number;
    name?: string;
    access?: boolean;
  }>;
}>;

export type AccessProgramBatchResponse = Record<string, AccessProgramResponse>;

const DEFAULT_USER_ACCESS_BATCH_CHUNK_SIZE = 40;

export function normalizeUserAccessMemberIds(
  memberIds: Array<number | string | null | undefined>,
) {
  return Array.from(
    new Set(
      memberIds
        .map((memberId) => Number(memberId || 0))
        .filter((memberId) => Number.isFinite(memberId) && memberId > 0),
    ),
  );
}

export function buildUserAccessBatchEndpoint(
  apiBase: string,
  memberIds: Array<number | string | null | undefined>,
) {
  const normalizedApiBase = String(apiBase || "").trim().replace(/\/+$/, "");
  if (!normalizedApiBase) return "";

  const ids = normalizeUserAccessMemberIds(memberIds);
  if (ids.length === 0) return "";

  const params = new URLSearchParams();
  ids.forEach((memberId) => {
    params.append("ids[]", String(memberId));
  });

  return `${normalizedApiBase}/project/access/users?${params.toString()}`;
}

export function buildUserAccessBatchEndpoints(
  apiBase: string,
  memberIds: Array<number | string | null | undefined>,
  chunkSize = DEFAULT_USER_ACCESS_BATCH_CHUNK_SIZE,
) {
  const ids = normalizeUserAccessMemberIds(memberIds);
  if (ids.length === 0) return [];

  const safeChunkSize =
    Number.isFinite(chunkSize) && chunkSize > 0
      ? Math.floor(chunkSize)
      : DEFAULT_USER_ACCESS_BATCH_CHUNK_SIZE;

  const endpoints: string[] = [];
  for (let index = 0; index < ids.length; index += safeChunkSize) {
    const endpoint = buildUserAccessBatchEndpoint(
      apiBase,
      ids.slice(index, index + safeChunkSize),
    );
    if (endpoint) {
      endpoints.push(endpoint);
    }
  }

  return endpoints;
}

export function mergeBatchAccessPayloads(
  payloads: Array<AccessProgramBatchResponse | null | undefined>,
) {
  return payloads.reduce<AccessProgramBatchResponse>((acc, payload) => {
    if (!payload || typeof payload !== "object") {
      return acc;
    }

    Object.entries(payload).forEach(([memberId, response]) => {
      if (Array.isArray(response)) {
        acc[memberId] = response;
      }
    });

    return acc;
  }, {});
}

export function readBatchAccessResponses(
  payload: AccessProgramBatchResponse | null | undefined,
  memberIds: Array<number | string | null | undefined>,
) {
  return memberIds.map((memberId) => {
    const normalizedId = Number(memberId || 0);
    if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
      return [];
    }

    const response = payload?.[String(normalizedId)];
    return Array.isArray(response) ? response : [];
  });
}
